import { useMeta } from "@/hooks/use-meta";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { lazy, Suspense, useEffect, useState } from "react";
import { Camera, Shield, Wifi, Sun, Clock, Eye, ArrowRight, CheckCircle, Star, MapPin, Cloud, Building2, HardHat, Landmark, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import TrustBadges from "@/components/TrustBadges";
import SectionHeading from "@/components/SectionHeading";
import { VimeoLite } from "@/components/VimeoLite";
const cameraImage = "/jobcam-site-2-1280.webp";
const monitoringImage = "/jobcam-site-3-1280.webp";

const QuoteForm = lazy(() => import("@/components/QuoteForm"));
const FAQSection = lazy(() => import("@/components/FAQSection"));

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i: number) => ({ opacity: 1, y: 0, transition: { delay: i * 0.1, duration: 0.6 } }),
};

const solutions = [
  { icon: Camera, title: "Construction Site Cameras", desc: "HD cameras designed for harsh construction environments with real-time streaming and cloud storage.", to: "/construction-site-cameras" },
  { icon: Wifi, title: "Remote Monitoring", desc: "4G/5G connected cameras with live viewing from any device, anywhere in Australia.", to: "/remote-monitoring-cameras" },
  { icon: Clock, title: "Time-Lapse Cameras", desc: "Automated time-lapse documentation of your entire construction project from start to finish.", to: "/construction-time-lapse-cameras" },
  { icon: Shield, title: "Temporary Site Security", desc: "Rapid-deploy security camera systems for temporary construction sites and events.", to: "/temporary-site-security-cameras" },
  { icon: Eye, title: "24/7 Live Monitoring", desc: "Professional monitoring centre watching your site around the clock with instant response.", to: "/remote-monitoring-cameras" },
  { icon: Cloud, title: "Secure Cloud Storage", desc: "All footage is stored securely offsite in the cloud — with only a short-term upload buffer on the camera. If your camera is stolen or damaged, your recordings are safe and accessible.", to: "/cloud-storage" },
];

const stats = [
  { value: "2,500+", label: "Cameras Deployed" },
  { value: "98%", label: "Client Retention" },
  { value: "15+", label: "Years Experience" },
  { value: "24/7", label: "Live Monitoring" },
];

const testimonials = [
  { name: "James Mitchell", role: "Project Director, Lendlease", text: "JobCam's construction cameras have transformed how we manage our sites. The real-time monitoring has reduced theft by 85% across our projects.", rating: 5 },
  { name: "Sarah Chen", role: "Site Manager, Multiplex", text: "The solar cameras were perfect for our remote infrastructure project. Zero downtime and exceptional image quality even in extreme conditions.", rating: 5 },
  { name: "David Thompson", role: "Operations Manager, CPB Contractors", text: "The time-lapse feature has been invaluable for stakeholder reporting. Our clients love seeing their project progress in stunning detail.", rating: 5 },
  { name: "Emma Rodriguez", role: "Construction Manager, John Holland", text: "Setup took less than a day and the live monitoring centre caught two break-in attempts in the first month. Genuinely the best ROI on our site.", rating: 5 },
  { name: "Michael O'Brien", role: "Site Supervisor, Hutchinson Builders", text: "Image clarity at night is incredible. The AI alerts filter out the wildlife noise so we only get pinged on things that actually matter.", rating: 5 },
  { name: "Priya Patel", role: "Project Engineer, BMD Group", text: "We hire JobCam units across every civil project now. Solar power means we can drop them anywhere along the alignment with zero infrastructure.", rating: 5 },
  { name: "Tom Anderson", role: "Asset Manager, City of Melbourne", text: "Procurement-friendly, Australian-hosted, and compliant out of the box. Reporting is straightforward and the support team is excellent.", rating: 5 },
  { name: "Lisa Nguyen", role: "Development Director, Mirvac", text: "The stakeholder time-lapse videos have been a hit with our investors. Doubles as serious site security — a no-brainer for us.", rating: 5 },
];

const homeFaqs = [
  { question: "How quickly can JobCam cameras be installed on my construction site?", answer: "JobCam offers rapid deployment with most construction site cameras installed within 24-48 hours of your order. Our experienced technicians handle everything from site assessment to camera positioning, 4G connectivity setup, and cloud platform configuration. For urgent requirements, we offer same-day installation in major Australian cities including Sydney, Melbourne, Brisbane, Perth, Adelaide, and Canberra." },
  { question: "Do your construction cameras work without mains power?", answer: "Yes! Our solar security cameras are specifically designed for construction sites without reliable mains power. Each unit features high-efficiency solar panels, industrial-grade batteries for overnight operation, and 4G connectivity for remote access. They're completely self-contained and can operate indefinitely in Australian conditions, making them ideal for greenfield sites, infrastructure projects, and remote locations." },
  { question: "What is the video quality of JobCam construction cameras?", answer: "All JobCam construction site cameras deliver 4K Ultra HD resolution as standard. Our cameras feature wide dynamic range (WDR) for challenging lighting conditions, infrared night vision for 24/7 surveillance, and weather-sealed housings rated IP67 for dust, rain, and extreme temperatures found on Australian construction sites." },
  { question: "Can I view my construction site cameras remotely?", answer: "Absolutely. Every JobCam camera connects via 4G/5G cellular networks to our secure cloud platform. You can view live feeds, review recorded footage, receive motion alerts, and generate time-lapse videos from any smartphone, tablet, or computer. Our remote monitoring cameras provide real-time access to your construction site from anywhere in Australia or the world." },
  { question: "How much do construction site cameras cost to hire?", answer: "JobCam hire plans start from $90 per camera per week for the Essential plan, which includes HD camera, 4G connectivity, cloud storage, and motion alerts. Professional plans start from $120/week. Pricing depends on the camera model, monitoring requirements, and contract duration. We offer volume discounts for multi-camera deployments. Contact us for a free site assessment and custom quote tailored to your construction project requirements." },
  { question: "Do you provide 24/7 professional monitoring?", answer: "Yes, JobCam offers optional 24/7 professional monitoring through our Australian-based monitoring centre. Our trained operators watch your live camera feeds around the clock, responding instantly to security breaches, unauthorised access, and safety incidents. This service integrates with our construction site cameras to provide comprehensive construction surveillance for your project." },
];

export default function Home() {
  useMeta({
    title: "Construction Camera Hire Australia | 24/7",
    description: "Hire solar-powered construction site cameras across Australia. 24/7 monitoring, 4G connected, flexible hire. Free quote in 24 hours.",
  });
  // Preload is now in index.html for faster LCP

  const [tIndex, setTIndex] = useState(0);
  const groupSize = 3;
  const groupCount = Math.ceil(testimonials.length / groupSize);
  useEffect(() => {
    const id = setInterval(() => setTIndex((i) => (i + 1) % groupCount), 5000);
    return () => clearInterval(id);
  }, [groupCount]);
  const visibleTestimonials = testimonials.slice(tIndex * groupSize, tIndex * groupSize + groupSize);

  return (
    <>
      {/* JSON-LD Organization + LocalBusiness (consolidated) */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        name: "JobCam Australia",
        url: "https://jobcam.com.au",
        telephone: "+61-1800-562-226",
        email: "support@jobcam.com.au",
        priceRange: "$$",
        openingHours: "Mo-Fr 08:00-18:00",
        description: "Australia's leading construction site camera hire and monitoring provider. Solar-powered, 4G-connected cameras deployed within 48 hours Australia-wide.",
        logo: {
          "@type": "ImageObject",
          url: "https://jobcam.com.au/jobcam-logo-400.webp",
          caption: "JobCam logo featuring bold white text on an orange background, representing a construction site camera provider.",
          creditText: "JobCam Australia",
          creator: { "@type": "Organization", name: "JobCam Australia" },
          copyrightNotice: "© JobCam Australia",
          acquireLicensePage: "https://jobcam.com.au/terms-and-conditions",
          license: "https://creativecommons.org/licenses/by-nc/4.0/",
        },
        address: {
          "@type": "PostalAddress",
          streetAddress: "176 Campbell St",
          addressLocality: "Belmont",
          addressRegion: "WA",
          postalCode: "6104",
          addressCountry: "AU",
        },
        geo: {
          "@type": "GeoCoordinates",
          latitude: -31.9473,
          longitude: 115.9275,
        },
        areaServed: ["Sydney", "Melbourne", "Brisbane", "Perth", "Adelaide", "Canberra", "Gold Coast", "Newcastle", "Wollongong", "Australia"],
        serviceType: ["Construction Site Cameras", "Solar Security Cameras", "Remote Monitoring", "Time-Lapse Cameras"],
        sameAs: ["https://www.linkedin.com/company/jobcam"],
        aggregateRating: {
          "@type": "AggregateRating",
          ratingValue: "4.9",
          reviewCount: "87",
          bestRating: "5",
          worstRating: "1",
        },
      }) }} />

      {/* JSON-LD WebSite Schema */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "WebSite",
        url: "https://jobcam.com.au/",
        name: "JobCam Australia",
        potentialAction: {
          "@type": "SearchAction",
          target: {
            "@type": "EntryPoint",
            urlTemplate: "https://jobcam.com.au/?s={search_term_string}",
          },
          "query-input": "required name=search_term_string",
        },
      }) }} />

      {/* Hero */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/hero-home-1280.webp"
            srcSet="/hero-home-750.webp 750w, /hero-home-1280.webp 1280w, /hero-home-1920.webp 1920w"
            sizes="(max-width: 768px) 100vw, (max-width: 1280px) 100vw, 1920px"
            alt="Solar-powered Jobcam construction site camera at an Australian build site"
            className="w-full h-full object-cover"
            width={1920}
            height={1080}
            fetchPriority="high"
            decoding="async"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background via-background/90 to-background/40" />
        </div>
        <div className="container relative z-10 py-20">
          <div className="max-w-2xl">
            <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }} className="inline-block px-4 py-1.5 rounded-full bg-secondary text-primary text-xs font-semibold uppercase tracking-widest mb-6">
              Australia's #1 Construction Camera Provider
            </motion.span>
            <motion.h1 initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.6 }} className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-6 leading-tight">
              Australia's Most Trusted{" "}
              <span className="text-gradient-orange">Construction Site Camera Hire</span>
            </motion.h1>
            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5, duration: 0.6 }} className="text-lg text-muted-foreground mb-8 leading-relaxed">
              Protect your construction site with Australia's most advanced camera systems. Solar-powered, 4G-connected, and monitored 24/7. Deployed in under 48 hours across Sydney, Melbourne, Brisbane, Perth, Adelaide & Canberra.
            </motion.p>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }} className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact">
                <Button variant="hero" size="xl">Get a Free Quote</Button>
              </Link>
              <a href="/Jobcam_Flyer.pdf" target="_blank" rel="noopener noreferrer" download>
                <Button variant="hero-outline" size="xl">Download Flyer <ArrowRight className="h-5 w-5" /></Button>
              </a>
            </motion.div>
            {/* Demo video link — prominent play button, not a text link */}
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.85 }} className="mt-5">
              <Link
                to="/watch-how-jobcam-protects"
                className="group inline-flex items-center gap-3 rounded-full border border-primary/40 bg-primary/10 pl-1.5 pr-5 py-1.5 text-foreground font-semibold hover:bg-primary/20 hover:border-primary transition-all"
                aria-label="Watch the demo: how JobCam protects your site"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-glow group-hover:scale-105 transition-transform">
                  <Play className="h-4 w-4 fill-current ml-0.5" />
                </span>
                <span>Watch the demo <span className="text-muted-foreground font-normal">· 2 min</span></span>
              </Link>
            </motion.div>
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }} className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-muted-foreground">
              <span className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-primary" /> Free Site Assessment</span>
              <span className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-primary" /> 48hr Install</span>
              <span className="flex items-center gap-2"><CheckCircle className="h-4 w-4 text-primary" /> No Lock-in</span>
            </motion.div>
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1 }} className="mt-4 text-sm">
              <span className="inline-block px-3 py-1.5 rounded-full bg-primary/10 border border-primary/30 text-primary font-semibold">
                From $90/week per camera
              </span>
            </motion.p>
          </div>
        </div>
      </section>

      <TrustBadges />

      {/* Who we serve */}
      <section className="py-20">
        <div className="container">
          <SectionHeading
            label="Who We Serve"
            title="Built for Every Type of Australian Construction"
            description="From tier-1 builders to local councils and civil works contractors, JobCam delivers tailored construction camera hire across the country."
          />
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                icon: HardHat,
                title: "Construction contractors",
                to: "/construction-site-cameras",
                cta: "Construction site cameras",
                desc: "Tier 1–3 builders, residential and commercial contractors choose JobCam for fast camera hire that protects high-value materials, plant and equipment from theft and vandalism. Our solar-powered, 4G-connected cameras deploy in 24–48 hours on any project — from a single-house build in suburban Sydney to a 30-storey commercial tower in the Melbourne CBD. Live streaming, motion-triggered alerts and time-lapse documentation are all included.",
              },
              {
                icon: Landmark,
                title: "Councils & local government",
                to: "/councils",
                cta: "Cameras for councils",
                desc: "Australian councils hire JobCam for compliance-ready, procurement-friendly construction camera coverage on roadworks, stormwater, parks, library and depot projects. We supply under existing government panels with PO billing, GST invoicing and Australian-hosted footage that meets the Australian Privacy Principles. One supervisor can monitor every active capital works site from a single dashboard.",
              },
              {
                icon: Building2,
                title: "Civil works & infrastructure",
                to: "/civil-works",
                cta: "Civil works camera hire",
                desc: "Tier-1 civil contractors hire JobCam for the longest, most remote infrastructure projects in Australia. Solar power and 4G/5G connectivity mean cameras work on highway alignments, rail corridors, bridges, pipelines and renewable energy builds without trenching for power. Cameras relocate along the alignment as work progresses — no extra mobilisation cost.",
              },
            ].map((segment, i) => (
              <motion.div key={segment.title} custom={i} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
                <Link to={segment.to} className="block glass-card rounded-xl p-8 h-full hover:border-glow transition-all duration-300 group">
                  <div className="h-12 w-12 rounded-lg bg-secondary flex items-center justify-center mb-5 group-hover:bg-primary/20 transition-colors">
                    <segment.icon className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="font-display text-xl font-bold text-foreground mb-3">{segment.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed mb-4">{segment.desc}</p>
                  <span className="inline-flex items-center gap-1 text-primary text-sm font-semibold group-hover:gap-2 transition-all">
                    {segment.cta} <ArrowRight className="h-4 w-4" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-20 bg-card">
        <div className="container">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, i) => (
              <motion.div key={stat.label} custom={i} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }} className="text-center">
                <p className="font-display text-4xl md:text-5xl font-bold text-primary mb-2">{stat.value}</p>
                <p className="text-sm text-muted-foreground">{stat.label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Video Showcase */}
      <section className="py-20">
        <div className="container">
          <SectionHeading
            label="See It In Action"
            title="How JobCam Protects Your Site"
            description="Watch how our construction site cameras deliver round-the-clock security and monitoring for Australia's biggest projects."
          />
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="max-w-4xl mx-auto"
          >
            <div className="relative rounded-2xl overflow-hidden shadow-card border border-border/50">
              <VimeoLite
                vimeoId="788949763"
                poster="/jobcam-protects-poster.webp"
                alt="JobCam Construction Site Camera Demo"
              />
            </div>
            <div className="text-center mt-4">
              <Link
                to="/watch-how-jobcam-protects"
                className="text-sm text-primary hover:text-primary/80 font-medium inline-flex items-center gap-1"
              >
                Watch the full demo <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Solutions */}
      <section className="py-20">
        <div className="container">
          <SectionHeading
            label="Our Solutions"
            title="Construction Camera Solutions for Every Site"
            description="From small residential builds to major infrastructure projects, JobCam provides tailored construction site camera solutions across Australia."
          />
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {solutions.map((s, i) => (
              <motion.div key={s.title} custom={i} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
                <Link to={s.to} className="block glass-card rounded-xl p-8 h-full hover:border-glow transition-all duration-300 group">
                  <div className="h-12 w-12 rounded-lg bg-secondary flex items-center justify-center mb-5 group-hover:bg-primary/20 transition-colors">
                    <s.icon className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="font-display text-xl font-bold text-foreground mb-3">{s.title}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed mb-4">{s.desc}</p>
                  <span className="inline-flex items-center gap-1 text-primary text-sm font-semibold group-hover:gap-2 transition-all">
                    Learn More <ArrowRight className="h-4 w-4" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Feature showcase */}
      <section className="py-20 bg-card">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div initial={{ opacity: 0, x: -30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}>
              <SectionHeading label="Why JobCam" title="Built for Australia's Toughest Sites" center={false} />
              <div className="space-y-5">
                {[
                  { title: "Solar & Battery Powered", desc: "No mains power required. Our solar security cameras operate independently in any location across Australia." },
                  { title: "4G/5G Connected", desc: "Built-in cellular connectivity means your cameras work anywhere with mobile coverage — no Wi-Fi needed." },
                  { title: "Weatherproof IP67 Rated", desc: "Engineered to withstand dust storms, heavy rain, extreme heat, and the harsh Australian construction environment." },
                  { title: "AI-Powered Alerts", desc: "Smart motion detection with AI filtering reduces false alarms and instantly notifies you of genuine security events." },
                ].map((f) => (
                  <div key={f.title} className="flex gap-4">
                    <div className="h-6 w-6 rounded-full bg-primary/20 flex items-center justify-center shrink-0 mt-0.5">
                      <CheckCircle className="h-4 w-4 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-display font-semibold text-foreground mb-1 text-base">{f.title}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
            <motion.div initial={{ opacity: 0, x: 30 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} className="relative">
              <img src={cameraImage} srcSet="/jobcam-site-2-750.webp 750w, /jobcam-site-2-1280.webp 1280w" sizes="(max-width: 1023px) 100vw, 700px" alt="JobCam solar-powered construction site security camera" className="rounded-xl shadow-card w-full" loading="lazy" decoding="async" width={1184} height={888} />
              <div className="absolute -bottom-6 -left-6 glass-card rounded-xl p-4 border-glow">
                <p className="text-sm font-semibold text-foreground">Live Feed Active</p>
                <p className="text-xs text-primary">4K · 24/7 · Solar Powered</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20">
        <div className="container">
          <SectionHeading label="Testimonials" title="Trusted by Australia's Leading Builders" description="See why major construction companies choose JobCam for their site security and monitoring needs." />
          <div className="relative min-h-[280px] md:min-h-[260px]">
            <AnimatePresence mode="wait">
              <motion.div
                key={tIndex}
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -12 }}
                transition={{ duration: 0.6, ease: "easeOut" }}
                className="grid grid-cols-1 md:grid-cols-3 gap-6"
              >
                {visibleTestimonials.map((t) => (
                  <div key={t.name} className="glass-card rounded-xl p-8">
                    <div className="flex gap-1 mb-4">
                      {Array.from({ length: t.rating }).map((_, j) => (
                        <Star key={j} className="h-4 w-4 fill-primary text-primary" />
                      ))}
                    </div>
                    <p className="text-muted-foreground text-sm leading-relaxed mb-6">"{t.text}"</p>
                    <div>
                      <p className="font-semibold text-foreground text-sm">{t.name}</p>
                      <p className="text-xs text-muted-foreground">{t.role}</p>
                    </div>
                  </div>
                ))}
              </motion.div>
            </AnimatePresence>
            <div className="flex justify-center gap-2 mt-8">
              {Array.from({ length: groupCount }).map((_, i) => (
                <button
                  key={i}
                  onClick={() => setTIndex(i)}
                  aria-label={`Show testimonials group ${i + 1}`}
                  className={`h-2 rounded-full transition-all ${i === tIndex ? "w-8 bg-primary" : "w-2 bg-muted-foreground/30"}`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Locations */}
      <section className="py-20 bg-card">
        <div className="container">
          <SectionHeading label="Australia-Wide" title="Construction Camera Hire in Every Major City" description="JobCam provides construction site cameras with local installation and support across Australia." />
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            {["Sydney", "Melbourne", "Brisbane", "Perth", "Adelaide", "Canberra"].map((city, i) => (
              <motion.div key={city} custom={i} variants={fadeUp} initial="hidden" whileInView="visible" viewport={{ once: true }}>
                <Link to={`/locations/${city.toLowerCase()}`} className="glass-card rounded-xl p-6 text-center hover:border-glow transition-all duration-300 block group">
                  <MapPin className="h-6 w-6 text-primary mx-auto mb-3 group-hover:scale-110 transition-transform" />
                  <p className="font-display font-semibold text-foreground">{city}</p>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Quote form */}
      <section className="py-20">
        <div className="container">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <SectionHeading label="Get Started" title="Request Your Free Site Assessment" center={false} />
              <p className="text-muted-foreground leading-relaxed mb-6">
                Our construction camera experts will assess your site requirements and provide a custom solution tailored to your project. Free site visits available across all major Australian cities.
              </p>
              <img src={monitoringImage} srcSet="/jobcam-site-3-750.webp 750w, /jobcam-site-3-1280.webp 1280w, /jobcam-site-3-1920.webp 1920w" sizes="(max-width: 1023px) 100vw, 700px" alt="Construction site camera monitoring at night with infrared lighting" className="rounded-xl shadow-card w-full" loading="lazy" decoding="async" width={1920} height={1280} />
            </div>
            <Suspense fallback={<div className="min-h-[600px]" />}>
              <QuoteForm />
            </Suspense>
          </div>
        </div>
      </section>

      {/* SEO Content */}
      <section className="py-20 bg-card">
        <div className="container max-w-4xl">
          <h2 className="font-display text-3xl font-bold text-foreground mb-8">Australia's Premier Construction Site Camera Provider</h2>
          <div className="prose prose-invert max-w-none space-y-6 text-muted-foreground leading-relaxed">
            <p>JobCam is Australia's leading provider of <strong className="text-foreground">construction site cameras</strong>, offering comprehensive surveillance and monitoring solutions for the building and construction industry. Our <strong className="text-foreground">construction site security cameras</strong> are trusted by major construction companies, project managers, developers, and infrastructure companies across Sydney, Melbourne, Brisbane, Perth, Adelaide, and Canberra.</p>
            <p>Whether you need a single <strong className="text-foreground">construction camera</strong> for a residential build or a network of <strong className="text-foreground">site security cameras</strong> for a major infrastructure project, JobCam delivers reliable, high-quality <strong className="text-foreground">construction surveillance</strong> solutions tailored to your specific requirements. Our <strong className="text-foreground">solar security cameras</strong> are particularly popular for greenfield sites and remote locations where mains power isn't available.</p>
            <p>Every JobCam system includes professional installation, 4G connectivity, cloud storage, and access to our intuitive monitoring platform. Our cameras provide crystal-clear footage day and night, with AI-powered motion detection that distinguishes between genuine security threats and false alarms caused by wildlife, weather, or authorised personnel.</p>
            <h3 className="font-display text-xl font-bold text-foreground mt-8">Construction Camera Solutions for Every Project Type</h3>
            <p>From <strong className="text-foreground">building site CCTV</strong> to advanced remote monitoring systems, JobCam's range covers every construction security need. Our <strong className="text-foreground">temporary site security cameras</strong> offer rapid deployment for short-term projects, while our long-term hire options provide cost-effective monitoring throughout your entire build program.</p>
            <p>The construction industry faces unique security challenges: valuable equipment and materials on-site, changing personnel, multiple access points, and the need for after-hours protection. JobCam's <strong className="text-foreground">construction site security cameras</strong> address all these challenges with intelligent monitoring, instant alerts, and professional response capabilities.</p>
            <h3 className="font-display text-xl font-bold text-foreground mt-8">Why Australian Builders Choose JobCam</h3>
            <p>With over 15 years of experience in the Australian construction industry, JobCam understands the unique requirements of local building sites. Our cameras are engineered to perform in Australia's extreme weather conditions, from scorching summers to heavy winter rains. We offer nationwide coverage with local technicians in every major city, ensuring rapid installation and responsive maintenance support.</p>
            <p>Our commitment to innovation means we're constantly developing new features and capabilities. Recent additions include AI-powered safety compliance monitoring, automated progress reporting, and integration with popular project management platforms like Procore and Aconex.</p>
          </div>
        </div>
      </section>

      <Suspense fallback={<div className="min-h-[400px]" />}>
        <FAQSection faqs={homeFaqs} />
      </Suspense>
    </>
  );
}
